import { ReactNode } from 'react';
import {
  EnterpriseStructure,
  OwnershipType,
  beneficialOwnerFormSchema,
  businessDetailsFormSchema,
  contactDetailsSchema,
  eligibilitySchema,
  financeSchema,
  guarantorDetailsFormSchema,
  householdIncomeCashSchema,
  householdIncomeLoanSchema,
  identificationFormSchema,
  incomeReviewSchema,
  incomeVerificationFormSchema,
  ownershipDetailsFormSchema,
  repaymentAccountWithTermsSchema,
  summarySchema,
} from '@brighte/validation';
import IncomeReview from '@steps/OpenBankingIncomeDetails/OpenBankingIncomeDetails';
import { APPLICATION_STEPS } from '@utils';
import {
  BeneficialOwner,
  BusinessDetails,
  ContactDetails,
  Eligibility,
  FinancePurchase,
  GuarantorDetails,
  Household,
  Identification,
  IncomeVerification,
  OwnershipDetails,
  RepaymentAccount,
  Summary,
} from '../../steps';

export const formStepSchemaMap = {
  [APPLICATION_STEPS.CONTACT_DETAILS]: {
    component: ContactDetails,
    schema: contactDetailsSchema,
  },
  [APPLICATION_STEPS.FINANCE_PURCHASE]: {
    component: FinancePurchase,
    schema: financeSchema,
  },
  [APPLICATION_STEPS.ELIGIBILITY]: {
    component: Eligibility,
    schema: eligibilitySchema,
  },
  [APPLICATION_STEPS.BUSINESS_DETAILS]: {
    component: BusinessDetails,
    schema: businessDetailsFormSchema,
  },
  [APPLICATION_STEPS.OWNERSHIP_DETAILS]: {
    component: OwnershipDetails,
    schema: ownershipDetailsFormSchema,
  },
  [APPLICATION_STEPS.REPAYMENT_ACCOUNT]: {
    component: RepaymentAccount,
    schema: repaymentAccountWithTermsSchema,
  },
  [APPLICATION_STEPS.IDENTIFICATION]: {
    component: Identification,
    schema: identificationFormSchema,
  },
  [APPLICATION_STEPS.INCOME_VERIFICATION]: {
    component: IncomeVerification,
    schema: incomeVerificationFormSchema,
  },
  [APPLICATION_STEPS.HOUSEHOLD]: {
    component: Household,
    schema: householdIncomeLoanSchema,
  },
  [APPLICATION_STEPS.HOUSEHOLD_CASH]: {
    component: Household,
    schema: householdIncomeCashSchema,
  },
  [APPLICATION_STEPS.SUMMARY]: {
    component: Summary,
    schema: summarySchema,
  },
  [APPLICATION_STEPS.INCOME_REVIEW]: {
    component: IncomeReview,
    schema: incomeReviewSchema,
  },
  [APPLICATION_STEPS.BENEFICIAL_OWNER]: {
    component: BeneficialOwner,
    schema: beneficialOwnerFormSchema,
  },
  [APPLICATION_STEPS.GUARANTOR_DETAILS]: {
    component: GuarantorDetails,
    schema: guarantorDetailsFormSchema,
  },
};

export const generateSteps = (steps: string[]): ReactNode[] =>
  steps.map(step => {
    const Component = formStepSchemaMap[step].component;
    return <Component key={step} />;
  });

const shouldIncludeBeneficialOwner = (
  entityStructure?: EnterpriseStructure,
  ownershipType?: OwnershipType,
): boolean => {
  if (!entityStructure) return false;

  switch (entityStructure) {
    case EnterpriseStructure.Partnership:
      return ownershipType === OwnershipType.Corporate;
    case EnterpriseStructure.ProprietaryLimitedCompany:
      return true;
    case EnterpriseStructure.Trust:
      return true;
    default:
      return false;
  }
};

const shouldIncludeGuarantor = (entityStructure?: EnterpriseStructure, ownershipType?: OwnershipType): boolean => {
  if (!entityStructure) return false;

  switch (entityStructure) {
    case EnterpriseStructure.Partnership:
      return ownershipType === OwnershipType.Corporate;
    case EnterpriseStructure.ProprietaryLimitedCompany:
      return true;
    case EnterpriseStructure.Trust:
      return ownershipType === OwnershipType.Corporate;
    default:
      return false;
  }
};

export const generateBusinessStepsVariants = (
  entityStructure?: EnterpriseStructure,
  ownershipType?: OwnershipType,
): string[] => {
  const steps = [
    APPLICATION_STEPS.CONTACT_DETAILS,
    APPLICATION_STEPS.FINANCE_PURCHASE,
    APPLICATION_STEPS.ELIGIBILITY,
    APPLICATION_STEPS.BUSINESS_DETAILS,
    APPLICATION_STEPS.OWNERSHIP_DETAILS,
  ];

  if (shouldIncludeBeneficialOwner(entityStructure, ownershipType)) {
    steps.push(APPLICATION_STEPS.BENEFICIAL_OWNER);
  }

  if (shouldIncludeGuarantor(entityStructure, ownershipType)) {
    steps.push(APPLICATION_STEPS.GUARANTOR_DETAILS);
  }

  steps.push(APPLICATION_STEPS.REPAYMENT_ACCOUNT, APPLICATION_STEPS.SUMMARY);

  return steps;
};

export const businessSteps = [
  APPLICATION_STEPS.CONTACT_DETAILS,
  APPLICATION_STEPS.FINANCE_PURCHASE,
  APPLICATION_STEPS.ELIGIBILITY,
  APPLICATION_STEPS.BUSINESS_DETAILS,
  APPLICATION_STEPS.OWNERSHIP_DETAILS,
  APPLICATION_STEPS.REPAYMENT_ACCOUNT,
  APPLICATION_STEPS.SUMMARY,
];

export const jointResidentialSteps = [
  APPLICATION_STEPS.CONTACT_DETAILS,
  APPLICATION_STEPS.FINANCE_PURCHASE,
  APPLICATION_STEPS.ELIGIBILITY,
  APPLICATION_STEPS.IDENTIFICATION,
  APPLICATION_STEPS.INCOME_VERIFICATION,
  APPLICATION_STEPS.SUMMARY,
];

export const primaryResidentialSteps = [
  APPLICATION_STEPS.CONTACT_DETAILS,
  APPLICATION_STEPS.FINANCE_PURCHASE,
  APPLICATION_STEPS.ELIGIBILITY,
  APPLICATION_STEPS.IDENTIFICATION,
  APPLICATION_STEPS.INCOME_VERIFICATION,
  APPLICATION_STEPS.HOUSEHOLD,
  APPLICATION_STEPS.REPAYMENT_ACCOUNT,
  APPLICATION_STEPS.SUMMARY,
];

export const primaryResidentialCashPaymentSteps = [
  APPLICATION_STEPS.CONTACT_DETAILS,
  APPLICATION_STEPS.FINANCE_PURCHASE,
  APPLICATION_STEPS.ELIGIBILITY,
  APPLICATION_STEPS.IDENTIFICATION,
  APPLICATION_STEPS.INCOME_VERIFICATION,
  APPLICATION_STEPS.HOUSEHOLD_CASH,
  APPLICATION_STEPS.SUMMARY,
];

export const primaryOpenBankingResidentialSteps = [
  APPLICATION_STEPS.CONTACT_DETAILS,
  APPLICATION_STEPS.FINANCE_PURCHASE,
  APPLICATION_STEPS.ELIGIBILITY,
  APPLICATION_STEPS.IDENTIFICATION,
  APPLICATION_STEPS.INCOME_VERIFICATION,
  APPLICATION_STEPS.HOUSEHOLD,
  APPLICATION_STEPS.REPAYMENT_ACCOUNT,
  APPLICATION_STEPS.INCOME_REVIEW,
  APPLICATION_STEPS.SUMMARY,
];

export const jointOpenBankingResidentialSteps = [
  APPLICATION_STEPS.CONTACT_DETAILS,
  APPLICATION_STEPS.FINANCE_PURCHASE,
  APPLICATION_STEPS.ELIGIBILITY,
  APPLICATION_STEPS.IDENTIFICATION,
  APPLICATION_STEPS.INCOME_VERIFICATION,
  APPLICATION_STEPS.INCOME_REVIEW,
  APPLICATION_STEPS.SUMMARY,
];
